import axios from "axios";
import AppConfig from "../helpers/config";
import { startApiLoading, stopApiLoading } from "../redux/slices/requestSlice";

const axiosApiInstance = axios.create();

export const post = (api, data, source) => {
    if (source) {
        const config = { cancelToken: source.token };
        return axiosApiInstance.post(api, data, config);
    }
    return axiosApiInstance.post(api, data);
};

export const postWithoutBody = (api) => {
    return axiosApiInstance.post(api);
};

export const get = (api, source, config = {}) => {
    // `config` lets callers pass per-request options (e.g. an X-Locale header
    // for the CMS static pages) without reaching for raw axios and losing the
    // auth / loading / cancel interceptors.
    if (source) {
        return axiosApiInstance.get(api, { ...config, cancelToken: source.token });
    }
    return axiosApiInstance.get(api, config);
};

axiosApiInstance.interceptors.request.use(
    async (config) => {
        // let params = null;
        const value = localStorage.getItem("auth_token");
        const store = (await import("../redux")).default; // Dynamically import store
        config.headers = {
            ...config.headers,
            Authorization: `Bearer ${value}`,
            'Content-Type': 'application/json',
        };

        // Capture original params BEFORE modification
        // if (config.method === "post") {
        //     params = config.data;
        // } else if (config.method === "get") {
        //     params = config.params;
        // }

        if (config.method === "post") {
            // The persisted store is absent before redux-persist first writes,
            // and after the app-version check calls localStorage.clear(). Both
            // used to throw here and fail the request outright.
            const readPersisted = (key) => {
                try {
                    const local = JSON.parse(
                        localStorage.getItem("persist:gt-web:rootApp")
                    );
                    return local?.[key] ? JSON.parse(local[key]) : null;
                } catch {
                    return null;
                }
            };

            const currency = readPersisted("loginconfig")?.currency?.code;

            if (
                config?.url?.includes("/SearchOnlyListing") ||
                config?.url?.includes("/GetPackageDetails") ||
                config?.url?.includes("/MoreFlightsSearch") ||
                config?.url?.includes("/InsertFavorite") ||
                config?.url?.includes("/DeleteFavorite")
            ) {
                const transaction_id = readPersisted("appconfig")?.transaction_id;
                config.data = {
                    ...config.data,
                    transaction_id,
                    currency,
                };
            }
        }

        // Only mint a token when the caller has not supplied one via
        // post(api, data, source) / get(api, source) — overwriting it made
        // those parameters useless, because the caller's source could never
        // cancel the request it was created for.
        const source = config.cancelToken ? null : axios.CancelToken.source();
        if (source) {
            config.cancelToken = source.token;
        }

        // Dispatch startApiLoading with cancelSource and the API URL
        store.dispatch(
            startApiLoading({
                cancelSource: source,
                apiUrl: config.url, // Include the API URL in the payload
                // params: params,
                method: config.method,
            }),
        );

        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for API calls
axiosApiInstance.interceptors.response.use(
    async (response) => {
        const store = (await import("../redux")).default; // Dynamically import store
        
        // Only stop loading for non-SearchOnlyListing requests
        if (!response.config.url?.includes("/SearchOnlyListing")) {
            store.dispatch(stopApiLoading());
        }
        
        return response;
    },
    async function (error) {
        const store = (await import("../redux")).default; // Dynamically import store
        const originalRequest = error.config;

        if (error?.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const res = await postWithoutBody(
                    `${AppConfig.baseApiURL}/Authenticate/GetAccessToken`
                );
                localStorage.setItem("auth_token", res.data.access_token);
                axios.defaults.headers.common["Authorization"] =
                    "Bearer " + res.data.access_token;
                return axiosApiInstance(originalRequest);
            } catch (refreshError) {
                // Without this the rejection escaped before stopApiLoading ran,
                // leaving the global loader up for the rest of the session.
                store.dispatch(stopApiLoading());
                return Promise.reject(refreshError);
            }
        }
        if (axios.isCancel(error)) {
            console.warn('Request canceled:', error.message);
        }

        store.dispatch(stopApiLoading());
        return Promise.reject(error);
    }
);
